import { PrimaryButton } from './ui'

export const MFA_CALL_CENTRE_DISPLAY = '1300 296 296'
export const MFA_CALL_CENTRE_TEL = 'tel:1300296296'

export type MfaCallCentreMobileReason = 'missing' | 'wrong'

type MfaCallCentreMobilePanelProps = {
  errorMessage?: string | null
  isSyncing?: boolean
  /** `missing` = no usable AU mobile; `wrong` = customer says the shown number is not theirs. */
  reason?: MfaCallCentreMobileReason
  onUpdatedNumber: () => void
}

/**
 * Call-centre guidance when Entsia has no usable Australian mobile for SMS MFA,
 * or the customer says the enrolled number is wrong.
 */
export function MfaCallCentreMobilePanel({
  errorMessage,
  isSyncing = false,
  reason = 'missing',
  onUpdatedNumber,
}: MfaCallCentreMobilePanelProps) {
  return (
    <div className="w-full max-w-[768px] rounded-2xl border border-border-primary bg-background-primary px-[20px] py-[24px] shadow-xs">
      {reason === 'wrong' ? (
        <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
          If that is not your mobile, or you did not receive a text, please call our customer service
          centre so we can update the number on your policy. We cannot change it from this screen.
        </p>
      ) : (
        <p className="m-0 font-body text-sm leading-sm text-text-secondary-700">
          We could not set up text-message security because there is no valid Australian mobile number
          on your policy (contact phone or mobile).
        </p>
      )}
      <p className="mt-4 m-0 font-body text-sm leading-sm text-text-secondary-700">
        Please call our customer service centre on{' '}
        <a className="font-semibold text-foreground-brand-solid" href={MFA_CALL_CENTRE_TEL}>
          {MFA_CALL_CENTRE_DISPLAY}
        </a>{' '}
        so we can confirm your identity and update the mobile on your policy. When that is done,
        return here and select <strong>I’ve updated my number</strong>.
      </p>

      {errorMessage ? (
        <div
          className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-900 font-body text-sm"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href={MFA_CALL_CENTRE_TEL}
          className="inline-flex w-full sm:w-auto items-center justify-center rounded-xl bg-foreground-brand-solid px-5 py-3 font-body text-sm font-semibold text-white no-underline"
        >
          Call {MFA_CALL_CENTRE_DISPLAY}
        </a>
        <PrimaryButton
          type="button"
          className="w-full sm:w-auto"
          disabled={isSyncing}
          onClick={onUpdatedNumber}
        >
          {isSyncing ? 'Checking…' : 'I’ve updated my number'}
        </PrimaryButton>
      </div>
    </div>
  )
}
